"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/shared/lib/api";
import { cn } from "@/shared/lib/utils";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import { Button } from "@/shared/ui/buttons/button";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/shared/ui/modals/dialog";
import { useUserProfile } from "@/hooks/useAuth";
import { useLfgLftPost } from "@/hooks/useLfgLft";
import { useRouter } from "@/i18n/navigation";
import { useChatSelectionStore } from "@/entities/conversation";
import { isChatAvailable } from "@/features/chat-portal/lib/chat-availability";
import { LfgChatCard, type LfgChatCardProps } from "./lfg-chat-card";
import { RespondMessageField } from "./respond-message-field";

export interface WriteTarget {
  postId: string;
  /** Объявление уже на руках (карточка доски); из «Заявок» его нет — подтянется по id. */
  post?: LfgLftPostResponse;
  toUserId: string;
  toName: string;
  /** Что стоит в кавычках заготовки подписи: «Ищет игроков», «Приглашение в команду». */
  kindLabel: string;
  card?: Pick<LfgChatCardProps, "header" | "description" | "meta">;
  /** «Написать» из «Заявок»: сервер соберёт фрейм из этой заявки. */
  applicationId?: string;
  /** С кем откроется чат, если это не тот, чьё имя во фрейме (у команды пишем капитану). */
  recipientName?: string;
}

function WriteBody({ target, onClose }: { target: WriteTarget; onClose: () => void }) {
  const t = useTranslations("LfgBoard");
  const router = useRouter();
  const fetched = useLfgLftPost(target.post ? null : target.postId);
  const post = target.post ?? fetched.data ?? null;
  const [note, setNote] = useState(() => t("write.note", { kind: target.kindLabel, name: target.toName }));
  const [pending, setPending] = useState(false);

  const send = async () => {
    if (pending) return;
    setPending(true);
    try {
      const { data } = await api.post<{ id?: string } | null>(
        `/conversations/direct/${encodeURIComponent(target.toUserId)}/lfg-card/${encodeURIComponent(target.postId)}`,
        { body: note.trim() || null, applicationId: target.applicationId ?? null },
      );
      if (!data?.id) throw new Error("conversation id missing");
      useChatSelectionStore.getState().selectConversation(data.id);
      onClose();
      router.push("/chat");
    } catch (e) {
      toast.error(extractApiErrorMessage(e, t("write.error")));
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
      className="flex min-w-0 flex-col"
    >
      <div className="flex min-w-0 flex-col gap-4 px-[18px] pb-3.5">
        {post ? (
          <LfgChatCard post={post} preview compact tone="board" {...target.card} />
        ) : fetched.isError ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("chatCard.closed")}</p>
        ) : (
          <Skeleton className="h-36 w-full rounded-xl" />
        )}
        <RespondMessageField value={note} onChange={setNote} />
      </div>
      <div className="flex items-center gap-2.5 border-t border-border px-[18px] pt-3 pb-4">
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{t("write.to", { name: target.recipientName ?? target.toName })}</span>
        <Button type="button" variant="ghost" onClick={onClose}>{t("card.cancel")}</Button>
        <Button type="submit" disabled={pending || !post} className="bg-highlight font-bold text-primary-foreground hover:bg-highlight/90">
          {t("respond.send")}
        </Button>
      </div>
    </form>
  );
}

/** «Отклик на объявление» по кнопке «Написать» (макет доски): фрейм объявления + подпись → сообщение в чат. */
export function WriteDialog({ target, onClose }: { target: WriteTarget | null; onClose: () => void }) {
  const t = useTranslations("LfgBoard");
  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="gap-0 p-0 sm:max-w-[440px]">
        <DialogHeader className="px-[18px] pt-4 pb-3">
          <DialogTitle className="font-heading text-[22px]">{t("respond.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("respond.signature")}</DialogDescription>
        </DialogHeader>
        {target && <WriteBody key={`${target.postId}-${target.toUserId}`} target={target} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

/** Кнопка «Написать» доски: открывает модалку отклика. Гейт тот же, что у чата: закрыт чат или пишем себе — кнопки нет. */
export function WriteAboutPostButton({
  target,
  label,
  className,
  onOpen,
}: {
  target: Omit<WriteTarget, "toUserId"> & { toUserId?: string | null };
  label: string;
  className?: string;
  /** Модалку держит родитель (в «Заявках» она заменяет диалог заявок, а не ложится поверх). */
  onOpen?: (target: WriteTarget) => void;
}) {
  const { data: me } = useUserProfile();
  const [open, setOpen] = useState(false);
  const toUserId = target.toUserId ? String(target.toUserId).trim() : "";
  if (!isChatAvailable() || !toUserId || !me?.id || String(me.id) === toUserId) return null;
  return (
    <>
      <Button type="button" variant="outline" className={cn("h-[34px]", className)} onClick={() => (onOpen ? onOpen({ ...target, toUserId }) : setOpen(true))}>
        <MessageCircle className="mr-1 h-4 w-4 shrink-0" />
        {label}
      </Button>
      {!onOpen && <WriteDialog target={open ? { ...target, toUserId } : null} onClose={() => setOpen(false)} />}
    </>
  );
}
